import React from 'react';
import PropTypes from 'prop-types';
import { ErrorMessage, Formik } from 'formik';

import { addSalePoint, getSchoolsByGrade } from '../../utils/api/salePointApi';
import PostalCodeFields from './PostalCodeFields';
import { initValidator } from './salePointValidator';
import GenericModal from '../ibok-core/module/GenericModal';

const prefetchSchools = () => Promise.all([
  getSchoolsByGrade('university'),
  getSchoolsByGrade('college'),
]);

const isStudyLevelSelected = (studyLevel, schools) => studyLevel !== 'default'
  && Object.keys(schools)
    .includes(studyLevel);

class AddSalePointModal extends React.Component {
  state = {
    schools: {
      university: [],
      college: [],
    },
    apiError: null,
  };

  componentDidMount() {
    // Save some user-interaction time if schools are selected,
    // by pre-fetching all schools in the background
    prefetchSchools()
      .then((grades) => {
        this.setState({
          schools: {
            university: grades[0],
            college: grades[1],
          },
        });
      });
  }

  handleApiErrors = (err) => {
    const message = typeof err !== 'string' ? err.message : err;

    this.setState({ apiError: message });
  };

  onSubmit = (values) => {
    addSalePoint(values)
      .then((data) => this.props.onComplete(data))
      .catch(this.handleApiErrors);
  };

  render() {
    // validator needs the latest schools and should
    // therefore be reinitiated on each update
    const validator = initValidator(this.state.schools);

    return <GenericModal title="Nytt salgssted" onDismiss={this.props.onDismiss}>
      <Formik
        initialValues={{
          address: '',
          postalCode: '',
          postalCity: '',
          studyLevel: 'default',
          school: 'default',
        }}
        validate={(values) => validator(values)}
        onSubmit={this.onSubmit}
      >
        {(props) => {
          const {
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            setValues,
          } = props;

          return <form onSubmit={handleSubmit} className="vertical salepointform">
            <ErrorMessage name="global" component="p" className="form-error"/>
            {this.state.apiError && <p className="form-error">{this.state.apiError}</p>}

            <fieldset>
              <legend>Legg inn adresse</legend>
              <div className="form-field">
                <label htmlFor="address">Adresse</label>
                <input
                  type="text"
                  name="address"
                  id="address"
                  className="input is-light"
                  autoComplete="street-address"
                  placeholder="Olav Tryggvasons Gate"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.address}/>
              </div>
              <ErrorMessage name="address" component="p" className="form-error"/>

              <div className="form-field">
                <PostalCodeFields onChange={(postalValues) => {
                  // https://jaredpalmer.com/formik/docs/api/formik#setvalues-fields-field-string-any-void
                  // Ensure that we also merge with the other existing values
                  setValues(Object.assign(values, postalValues));
                }}/>
                <ErrorMessage name="postalCity" component="p" className="form-error"/>
              </div>
            </fieldset>

            <fieldset>
              <legend>Eller endre ditt studiested</legend>
              <select
                onChange={handleChange}
                onBlur={handleBlur}
                name="studyLevel"
                id="studyLevel"
                className="input is-light"
                placeholder="Mitt studienivå"
                value={values.studyLevel}
              >
                <option value="default">Mitt studienivå</option>
                <option value="university">Universitet</option>
                <option value="college">Høgskole</option>
              </select>

              {(isStudyLevelSelected(values.studyLevel, this.state.schools)) && <select
                name="school"
                id="school"
                className="input is-light"
                value={values.school || 'default'}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="default" disabled>Velg et studiested</option>
                {this.state.schools[values.studyLevel].map((school) => <option
                  key={school.id}
                  value={school.id}
                >
                  {school.shortname} - {school.department} ({school.name})
                </option>)}
              </select>}
              {errors.school && <div className="form-error">{errors.school}</div>}
            </fieldset>

            <div className="columns is-reversed has-no-wrapping has-upper-margin">
              <button className="button" type="submit" disabled={!isValid}>Opprett</button>
              <button className="button plain" onClick={this.props.onDismiss} type="reset">Avbryt</button>
            </div>
          </form>;
        }}
      </Formik>
    </GenericModal>;
  }
}

AddSalePointModal.propTypes = {
  onDismiss: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default AddSalePointModal;
